import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Table, Row, Col, Card, CardBody, Input, Badge,
    Pagination, PaginationItem, PaginationLink, Label
} from "reactstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { getCookie } from "../../Helpers/Utils";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const ExpenseList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userData = getCookie("user_data") ? JSON.parse(getCookie("user_data")) : {};
    const userRole = userData?.role || "staff";

    const [expenses, setExpenses] = useState([]);
    const [summary, setSummary] = useState({ total_amount: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        type: null, search: "", from_date: null, to_date: null,
        min_amount: "", max_amount: "", sort: "DESC", page: 1, limit: 10
    });

    const typeOptions = [
        { value: "", label: "All Types" },
        { value: "expense", label: "General Expense" },
        { value: "investment", label: "Investment" }
    ];

    const customSelectStyles = {
        control: (base) => ({
            ...base, minHeight: '45px', height: '45px', borderRadius: '8px',
            borderColor: '#dee2e6', boxShadow: 'none', '&:hover': { borderColor: '#2e4a3d' }
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    const fetchExpenses = useCallback(async () => {
        dispatch(loadingTrue());
        try {
            const params = new URLSearchParams({
                page: filters.page,
                limit: filters.limit,
                sort: filters.sort,
                ...(filters.type && { type: filters.type }),
                ...(filters.search && { search: filters.search }),
                ...(filters.from_date && { from_date: dayjs(filters.from_date).format('YYYY-MM-DD') }),
                ...(filters.to_date && { to_date: dayjs(filters.to_date).format('YYYY-MM-DD') })
            });

            const res = await Handler({ method: "GET", url: `/expense/list.php?${params.toString()}` });

            if (res?.success) {
                setExpenses(res.data || []);
                setSummary(res.summary || { total_amount: 0 });
                setPagination({
                    current_page: parseInt(res.current_page),
                    total_pages: parseInt(res.total_pages),
                    total_records: parseInt(res.total_records)
                });
            } else {
                toast.error(res?.message || "Failed to fetch");
            }
        } catch {
            toast.error("Server error");
        } finally {
            dispatch(loadingFalse());
        }
    }, [filters, dispatch]);

    useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1 bg-light min-vh-100">
                <div className="page-header mb-4">
                    <h6 className="text-muted text-uppercase small fw-bold">Finance & Billing</h6>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold text-theme m-0">Expense Records</h3>
                        </div>
                        <Button className="px-4 shadow-sm" style={{ background: '#2e4a3d', border: 'none' }} onClick={() => navigate("/expenses/add")}>
                            <i className="bx bx-plus me-1"></i> Add Expense
                        </Button>
                    </div>
                </div>

                <Row className="mb-4 g-3">
                    <Col md={12}>
                        <Card className="border-0 shadow-sm rounded-4 bg-theme text-white text-center p-3">
                            <small className="opacity-75 text-uppercase fw-bold">Total Expense</small>
                            <h2 className="fw-bold mb-0">₹ {(summary?.total_amount || 0).toLocaleString()}</h2>
                        </Card>
                    </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-4 rounded-4">
                    <CardBody className="p-4">
                        <Row className="g-3 align-items-end">
                            <Col md={3}><Label className="small fw-bold">Search</Label><Input style={{ height: '45px' }} placeholder="Search..." value={filters.search} onChange={(e) => handleFilterChange("search", e.target.value)} /></Col>
                            <Col md={2}><Label className="small fw-bold">Type</Label><Select options={typeOptions} styles={customSelectStyles} value={typeOptions.find(o => o.value === (filters.type || ""))} onChange={(opt) => handleFilterChange("type", opt?.value || null)} /></Col>
                            <Col md={2}><Label className="small fw-bold">From</Label><DatePicker format="DD/MM/YYYY" value={filters.from_date} onChange={(val) => handleFilterChange("from_date", val)} slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }} /></Col>
                            <Col md={2}><Label className="small fw-bold">To</Label><DatePicker format="DD/MM/YYYY" value={filters.to_date} onChange={(val) => handleFilterChange("to_date", val)} slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } } } }} /></Col>
                            <Col md={1}><Button color="secondary" outline className="w-100" style={{ height: '45px' }} onClick={() => setFilters({ ...filters, type: null, search: "", from_date: null, to_date: null, page: 1 })}><i className="bx bx-reset"></i></Button></Col>
                        </Row>
                    </CardBody>
                </Card>

                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light text-muted small text-uppercase">
                                <tr>
                                    <th className="ps-4">#</th>
                                    {userRole === "admin" && <th>Staff</th>}
                                    <th>Type</th>
                                    <th>Amount</th>
                                    <th>Description</th>
                                    <th>Date</th>
                                    {userRole === "admin" && <th className="text-center">Action</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {expenses.map((exp, i) => (
                                    <tr key={exp.id}>
                                        <td>{(Number(filters.page) - 1) * Number(filters.limit) + i + 1}</td>
                                        {userRole === "admin" && <td><div className="fw-bold">{exp.staff.name}</div></td>}
                                        <td>{exp.type === "investment" ? <Badge color="success" pill>Investment</Badge> : <Badge color="warning" pill>{exp.type || "Expense"}</Badge>}</td>
                                        <td className="fw-bold">₹ {parseFloat(exp.amount).toLocaleString()}</td>
                                        <td>{exp.description}</td>
                                        <td>{dayjs(exp.created_at).format('DD MMM YYYY')}</td>
                                        {userRole === "admin" && (
                                            <td className="text-center">
                                                <Button color="light" size="sm" className="border shadow-sm" onClick={() => navigate(`/expenses/${exp.id}`)}><i className="bx bx-edit text-info"></i></Button>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Advanced Pagination */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            {/* Previous Button */}
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>

                            {/* Dynamic Page Rendering */}
                            {[...Array(pagination?.total_pages)].map((_, i) => {
                                const pageNumber = i + 1;
                                const isCurrent = pageNumber === filters.page;

                                // Logic to show: First, Last, Current, and neighbors
                                const shouldShow =
                                    pageNumber === 1 ||
                                    pageNumber === pagination?.total_pages ||
                                    (pageNumber >= filters.page - 1 && pageNumber <= filters.page + 1);

                                if (shouldShow) {
                                    return (
                                        <PaginationItem active={isCurrent} key={i}>
                                            <PaginationLink onClick={() => setFilters({ ...filters, page: pageNumber })}>
                                                {pageNumber}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                } else if (
                                    pageNumber === filters.page - 2 ||
                                    pageNumber === filters.page + 2
                                ) {
                                    // Render Ellipsis
                                    return (
                                        <PaginationItem disabled key={i}>
                                            <PaginationLink>...</PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                                return null;
                            })}

                            {/* Next Button */}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default ExpenseList;